import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ExpenseAttachment {
  id: string;
  expense_id: string;
  company_id: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export const useExpenseAttachments = (expenseId?: string | null) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["expense_attachments", expenseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_expense_attachments" as any)
        .select("*")
        .eq("expense_id", expenseId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as ExpenseAttachment[];
    },
    enabled: !!user && !!expenseId,
  });
};

export async function uploadExpenseAttachments(params: {
  expense_id: string;
  company_id: string;
  files: File[];
}): Promise<{ uploaded: number; failed: { name: string; error: string }[] }> {
  const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
  const failed: { name: string; error: string }[] = [];
  let uploaded = 0;

  for (const file of params.files) {
    try {
      const safeName = file.name.replace(/[^\w.\-]/g, "_");
      const filePath = `${params.company_id}/${params.expense_id}/${Date.now()}_${safeName}`;
      const { error: upErr } = await supabase.storage
        .from("expense-receipts")
        .upload(filePath, file, { upsert: false, contentType: file.type || undefined });
      if (upErr) throw upErr;

      const { error: insErr } = await supabase
        .from("project_expense_attachments" as any)
        .insert({
          expense_id: params.expense_id,
          company_id: params.company_id,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type || null,
          uploaded_by: userId,
        } as any);
      if (insErr) throw insErr;
      uploaded++;
    } catch (e: any) {
      failed.push({ name: file.name, error: e?.message || "Upload failed" });
    }
  }
  return { uploaded, failed };
}

export const useUploadExpenseAttachments = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { expense_id: string; files: File[] }) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;
      return uploadExpenseAttachments({
        expense_id: input.expense_id,
        company_id: companyId as unknown as string,
        files: input.files,
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["expense_attachments", vars.expense_id] });
    },
  });
};

export const useDeleteExpenseAttachment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (att: ExpenseAttachment) => {
      await supabase.storage.from("expense-receipts").remove([att.file_path]);
      const { error } = await supabase
        .from("project_expense_attachments" as any)
        .delete()
        .eq("id", att.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["expense_attachments", vars.expense_id] });
    },
  });
};
